  if (!customElements.get('fg-hover-mega-menu')) {
    class FgHoverMegaMenu extends HTMLElement {
      connectedCallback() {
        if (this._fgHmmInit) return;
        this._fgHmmInit = true;

        this.refreshElements();
        this._menuReady = false;
        // Module-level maps (below the class) — four menu instances share
        // fetched panel HTML instead of holding four copies.
        this._panelHtml = FgHoverMegaMenu._panelHtml;
        this._panelInflight = FgHoverMegaMenu._panelInflight;

        if (!this.tabs.length) return;

        // After theme menu-product-list inits (header.js), harden FG carousels only.
        window.requestAnimationFrame(() => this.hardenCarousels());

        this.tabs.forEach((tab) => {
          const index = Number(tab.getAttribute('data-fg-hmm-tab'));
          tab.addEventListener('mouseenter', () => this.scheduleHoverActivation(() => this.activateTab(index)));
          tab.addEventListener('mouseleave', () => this.cancelHoverActivation());
          tab.addEventListener('focus', () => this.activateTab(index));
          tab.addEventListener('keydown', (event) => this.onTabKeydown(event, tab));
          if (tab.tagName === 'BUTTON') {
            tab.addEventListener('click', (event) => {
              event.preventDefault();
              this.activateTab(index);
            });
          }
        });

        this.bindBrandTriggers();
        this.guardColumnScroll();

        const megaMenu = this.closest('.mega-menu');
        const details = this.closest('details');

        this._onMenuOpen = () => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => this.onMenuOpen());
          });
        };

        if (megaMenu) {
          this._megaMenuObserver = new MutationObserver(() => {
            if (megaMenu.hasAttribute('open')) this._onMenuOpen();
          });
          this._megaMenuObserver.observe(megaMenu, { attributes: true, attributeFilter: ['open'] });
        }

        if (details) {
          details.addEventListener('toggle', () => {
            if (details.open) this._onMenuOpen();
          });

          // Warm the active tab's default product row from the first
          // interaction with the trigger — pointer/keyboard intent lands
          // 100–300 ms before the panel opens, and the toggle above stays the
          // final fallback. Repeats are free (shared _panelHtml/_panelInflight
          // caches). Deliberately no idle/after-load warmup: speculative
          // fetches must not compete with the page's own LCP.
          const summary = details.querySelector(':scope > summary');
          if (summary) {
            const prefetch = () => this.prefetchTabPanels(this.getActiveTabIndex());
            ['pointerenter', 'focusin', 'pointerdown'].forEach((type) => {
              summary.addEventListener(type, prefetch);
            });
          }
        }

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (event) => {
            const blockEl = event.target;
            if (!this.contains(blockEl)) return;
            const tab =
              blockEl.querySelector('.fg-hmm__tab[data-fg-hmm-tab]') ||
              blockEl.closest('.fg-hmm__tab[data-fg-hmm-tab]');
            if (tab) {
              this.activateTab(Number(tab.getAttribute('data-fg-hmm-tab')));
              return;
            }
            // Selection landed on a middle panel, products panel, or promo
            // tile region — resolve the owning tab from its data attributes.
            const tagged =
              blockEl.closest('[data-fg-hmm-panel], [data-fg-hmm-tab]') ||
              blockEl.querySelector('[data-fg-hmm-panel], [data-fg-hmm-tab]');
            if (tagged) {
              const index = Number(
                tagged.getAttribute('data-fg-hmm-panel') ?? tagged.getAttribute('data-fg-hmm-tab')
              );
              if (!Number.isNaN(index)) this.activateTab(index, { force: true });
            }
          });
        }
      }

      disconnectedCallback() {
        this._megaMenuObserver?.disconnect();
        this.cancelHoverActivation();
        this._fgHmmInit = false;
      }

      refreshElements() {
        this.tabs = Array.from(this.querySelectorAll('.fg-hmm__tab[data-fg-hmm-tab]'));
        this.middlePanels = Array.from(this.querySelectorAll('[data-fg-hmm-panel]'));
        this.productPanels = Array.from(this.querySelectorAll('[data-fg-hmm-products]'));
        this.activeIndex = this.getActiveTabIndex();
      }

      getActiveTabIndex() {
        const activeTab = this.tabs.find((tab) => tab.classList.contains('is-active'));
        const index = activeTab ? Number(activeTab.getAttribute('data-fg-hmm-tab')) : 0;
        return Number.isNaN(index) ? 0 : index;
      }

      getMiddlePanel(tabIndex) {
        return this.middlePanels.find((panel) => Number(panel.getAttribute('data-fg-hmm-panel')) === tabIndex);
      }

      getProductsPanel(tabIndex) {
        return this.productPanels.find((panel) => Number(panel.getAttribute('data-fg-hmm-products')) === tabIndex);
      }

      bindBrandTriggers() {
        this.querySelectorAll('[data-fg-hmm-brand-trigger]').forEach((trigger) => {
          if (trigger.dataset.fgHmmBound === 'true') return;
          trigger.dataset.fgHmmBound = 'true';

          trigger.addEventListener('mouseenter', () => {
            // Synthetic mouseenter fires when logos are injected under the
            // cursor — ignore those so the default product row stays put.
            if (Date.now() < (this._suppressBrandHoverUntil || 0)) return;
            const tabIndex = Number(trigger.getAttribute('data-fg-hmm-tab'));
            const brandIndex = Number(trigger.getAttribute('data-fg-hmm-brand'));
            if (Number.isNaN(tabIndex) || Number.isNaN(brandIndex)) return;
            this.scheduleHoverActivation(() => this.activateBrand(tabIndex, brandIndex, true));
          });

          trigger.addEventListener('mouseleave', () => this.cancelHoverActivation());

          trigger.addEventListener('focus', () => {
            const tabIndex = Number(trigger.getAttribute('data-fg-hmm-tab'));
            const brandIndex = Number(trigger.getAttribute('data-fg-hmm-brand'));
            if (Number.isNaN(tabIndex) || Number.isNaN(brandIndex)) return;
            this.activateBrand(tabIndex, brandIndex, true);
          });

          trigger.addEventListener('keydown', (event) => this.onBrandKeydown(event, trigger));
        });
      }

      /**
       * Chrome sometimes natively resets a scrolled middle panel to 0 while
       * panel hydrations and image loads are still churning (no JS scroll
       * call involved — verified by patching scrollTop/scrollTo/
       * scrollIntoView/focus, all silent). Guard: remember each panel's
       * scroll position, and when a scroll event lands a visible panel on
       * exactly 0 with no recent user input, put it back. User scrolls
       * always arrive with wheel/pointer/touch/key activity, so real
       * scroll-to-top gestures are never overridden.
       */
      guardColumnScroll() {
        const markUserInput = () => {
          this._userScrollUntil = Date.now() + 400;
        };
        ['wheel', 'pointerdown', 'touchstart', 'keydown'].forEach((type) => {
          this.addEventListener(type, markUserInput, { passive: true, capture: true });
        });

        this.addEventListener(
          'scroll',
          (event) => {
            const panel = event.target;
            if (!(panel instanceof Element) || !panel.classList.contains('fg-hmm__middle-panel')) return;
            const top = panel.scrollTop;
            const userRecent = Date.now() < (this._userScrollUntil || 0);
            if (!userRecent && top === 0 && panel._fgLastScroll > 0 && panel.classList.contains('is-active')) {
              // Spurious native reset — restore (re-entrant event records below).
              panel.scrollTop = panel._fgLastScroll;
              return;
            }
            panel._fgLastScroll = top;
          },
          true
        );
      }

      /**
       * Hover intent — sweeping the cursor across tabs/logos shouldn't flicker
       * through panels. One shared timer suffices; only one element is hovered
       * at a time. Focus/click activation stays immediate.
       */
      scheduleHoverActivation(fn) {
        this.cancelHoverActivation();
        this._hoverTimer = setTimeout(() => {
          this._hoverTimer = null;
          fn();
        }, 120);
      }

      cancelHoverActivation() {
        if (this._hoverTimer) {
          clearTimeout(this._hoverTimer);
          this._hoverTimer = null;
        }
      }

      onTabKeydown(event, tab) {
        const keys = { ArrowDown: 1, ArrowUp: -1, Home: 0, End: 0 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        if (!this.tabs.length) return;

        let position = this.tabs.indexOf(tab);
        if (event.key === 'Home') position = 0;
        else if (event.key === 'End') position = this.tabs.length - 1;
        else position = (position + keys[event.key] + this.tabs.length) % this.tabs.length;

        this.tabs[position].focus();
      }

      onBrandKeydown(event, trigger) {
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (!(event.key in keys)) return;
        // Brand triggers are links — without this, arrows scroll the panel
        // instead of moving between logos.
        event.preventDefault();

        const middlePanel = trigger.closest('[data-fg-hmm-panel]');
        if (!middlePanel) return;
        const triggers = Array.from(middlePanel.querySelectorAll('[data-fg-hmm-brand-trigger]'));
        if (!triggers.length) return;

        const position = (triggers.indexOf(trigger) + keys[event.key] + triggers.length) % triggers.length;
        triggers[position].focus();
      }

      onMenuOpen() {
        this.refreshElements();
        this.bindBrandTriggers();
        this.hardenCarousels();
        const index = this.getActiveTabIndex();
        this.activateTab(index, { force: true, resetBrand: !this._menuReady });
        this._menuReady = true;
      }

      /**
       * Lazy auto-brand grids — placeholders carry data-fg-hmm-src pointing
       * at sections/fg-hmm-brands-tab.liquid (grid only). Product-panel stubs
       * are built client-side from each logo's href so trigger/panel indices
       * can never drift, and a second Liquid discovery pass is avoided.
       */
      hydrateBrands(tabIndex) {
        const middlePanel = this.getMiddlePanel(tabIndex);
        const productsPanel = this.getProductsPanel(tabIndex);
        if (!middlePanel) return Promise.resolve();

        const slot = middlePanel.querySelector('[data-fg-hmm-lazy-brands]');
        if (!slot) return Promise.resolve();

        const src = slot.getAttribute('data-fg-hmm-src');
        if (!src) return Promise.resolve();

        const inject = (html) => {
          const current = middlePanel.querySelector('[data-fg-hmm-lazy-brands]');
          if (!current) return;

          const template = document.createElement('template');
          template.innerHTML = html;
          const payload =
            template.content.querySelector('[data-fg-hmm-brands-payload]') ||
            template.content.querySelector('.shopify-section')?.querySelector('[data-fg-hmm-brands-payload]');
          if (!payload) {
            current.remove();
            return;
          }

          const gridWrap = payload.querySelector('[data-fg-hmm-brands-grid]') || payload;
          const grid = gridWrap.querySelector('.fg-hmm__brand-list') || gridWrap.querySelector('ul');

          if (grid) {
            // Section Rendering API URLs take no custom params, so the
            // endpoint renders the schema maximum and the tab's configured
            // "Max brands shown" is applied here — before triggers are bound
            // and before ensureBrandPanelsFromTriggers builds their stubs.
            const limit = Number(current.getAttribute('data-fg-hmm-brands-limit'));
            if (Number.isFinite(limit) && limit > 0) {
              grid.querySelectorAll(':scope > li').forEach((item, position) => {
                if (position >= limit) item.remove();
              });
            }

            grid.querySelectorAll('[data-fg-hmm-brand-trigger]').forEach((trigger) => {
              trigger.setAttribute('data-fg-hmm-tab', String(tabIndex));
            });
            const withHeading = current.hasAttribute('data-fg-hmm-brands-with-heading');
            if (withHeading) {
              const heading = current.querySelector('.fg-hmm__brand-set-heading');
              current.replaceChildren();
              if (heading) current.appendChild(heading);
              current.appendChild(grid);
              current.removeAttribute('data-fg-hmm-lazy-brands');
              current.removeAttribute('data-fg-hmm-src');
            } else {
              current.replaceWith(grid);
            }

            if (productsPanel) {
              this.ensureBrandPanelsFromTriggers(tabIndex, productsPanel, grid);
              productsPanel.setAttribute('data-fg-hmm-has-brands', '');
            }
          } else {
            current.remove();
          }

          // Logos injected under the cursor synthesize mouseenter — suppress
          // briefly, then keep the default product row active.
          this._suppressBrandHoverUntil = Date.now() + 600;
          this.cancelHoverActivation();
          this.refreshElements();
          this.bindBrandTriggers();
          this.showDefaultProducts(tabIndex);
        };

        if (FgHoverMegaMenu._brandsHtml.has(src)) {
          inject(FgHoverMegaMenu._brandsHtml.get(src));
          return Promise.resolve();
        }
        if (FgHoverMegaMenu._brandsInflight.has(src)) {
          return FgHoverMegaMenu._brandsInflight.get(src).then(() => {
            if (FgHoverMegaMenu._brandsHtml.has(src)) inject(FgHoverMegaMenu._brandsHtml.get(src));
          });
        }

        const promise = fetch(src)
          .then((response) => {
            if (!response.ok) throw new Error(`fg-hmm-brands-tab ${response.status}`);
            return response.text();
          })
          .then((html) => {
            FgHoverMegaMenu._brandsHtml.set(src, html);
            FgHoverMegaMenu._brandsInflight.delete(src);
            inject(html);
          })
          .catch(() => {
            FgHoverMegaMenu._brandsInflight.delete(src);
            slot.remove();
          });
        FgHoverMegaMenu._brandsInflight.set(src, promise);
        return promise;
      }

      /**
       * Build on-demand product stubs from brand logo hrefs so each trigger
       * has a matching panel without a second server-side brand discovery.
       */
      ensureBrandPanelsFromTriggers(tabIndex, productsPanel, grid) {
        const host = productsPanel.querySelector('[data-fg-hmm-brand-panels-host]');
        if (!host) return;

        const defaultPanel = this.findBrandPanel(productsPanel, -1);
        grid.querySelectorAll('[data-fg-hmm-brand-trigger]').forEach((trigger) => {
          const brandIndex = Number(trigger.getAttribute('data-fg-hmm-brand'));
          if (Number.isNaN(brandIndex)) return;
          if (this.findBrandPanel(productsPanel, brandIndex)) return;

          const href = trigger.getAttribute('href');
          if (!href) return;
          const src = href.includes('section_id=')
            ? href
            : href.includes('?')
              ? `${href}&section_id=fg-hmm-panel`
              : `${href}?section_id=fg-hmm-panel`;

          const panel = document.createElement('div');
          panel.className = 'fg-hmm__brand-products';
          panel.setAttribute('data-fg-hmm-brand-products', String(brandIndex));
          panel.setAttribute('data-fg-hmm-lazy', '');
          panel.setAttribute('data-fg-hmm-src', src);
          panel.hidden = true;

          const header = document.createElement('div');
          header.className = 'fg-hmm__products-header flex items-center justify-between';
          header.innerHTML = '<p class="fg-hmm__products-heading h5 font-heading" aria-hidden="true">…</p>';

          const loading = document.createElement('div');
          loading.className = 'fg-hmm__loading no-empty';
          loading.setAttribute('aria-hidden', 'true');
          loading.innerHTML = '<span class="fg-hmm__spinner"></span>';

          const fallback = document.createElement('a');
          fallback.href = href;
          fallback.className = 'fg-hmm__view-all fg-hmm__view-all--fallback';
          fallback.innerHTML =
            '<span class="fg-hmm__view-all-label">View all</span>';

          panel.append(header, loading, fallback);
          host.appendChild(panel);
        });

        // Keep the default row as the only active panel after stub insert.
        if (defaultPanel) {
          productsPanel.querySelectorAll('[data-fg-hmm-brand-products]').forEach((brandPanel) => {
            const index = Number(brandPanel.getAttribute('data-fg-hmm-brand-products'));
            const isDefault = index === -1;
            brandPanel.classList.toggle('is-active', isDefault);
            brandPanel.toggleAttribute('hidden', !isDefault);
          });
        }
      }

      findBrandPanel(productsPanel, brandIndex) {
        if (!productsPanel) return null;
        return Array.from(productsPanel.querySelectorAll('[data-fg-hmm-brand-products]')).find(
          (el) => Number(el.getAttribute('data-fg-hmm-brand-products')) === brandIndex
        );
      }

      showDefaultProducts(tabIndex) {
        const productsPanel = this.getProductsPanel(tabIndex);
        if (!productsPanel) return;
        const defaultPanel = this.findBrandPanel(productsPanel, -1);
        if (defaultPanel) {
          this.activateBrand(tabIndex, -1, true);
          return;
        }
        this.prefetchTabPanels(tabIndex);
      }

      /**
       * FG mega menu only — do not change theme MenuProductList defaults.
       * Trackpads often slip a few px on click; theme also slideTo()s on focusin,
       * which feels like a swipe before navigation.
       */
      hardenCarousels(root) {
        (root || this).querySelectorAll('menu-product-list[data-fg-hmm-carousel]').forEach((list) => {
          const swiperEl = list.querySelector('.swiper');
          const swiper = swiperEl?.swiper;
          if (!swiper) return;

          swiper.params.threshold = 20;
          swiper.params.shortSwipes = false;
          swiper.params.preventClicks = true;
          swiper.params.preventClicksPropagation = true;

          if (list.dataset.fgHmmClickSafe === 'true') return;
          list.dataset.fgHmmClickSafe = 'true';

          list.addEventListener(
            'focusin',
            (event) => {
              if (event.target.matches(':focus-visible')) return;
              const instance = list.querySelector('.swiper')?.swiper;
              if (!instance || typeof instance.slideTo !== 'function') return;

              const originalSlideTo = instance.slideTo;
              instance.slideTo = function () {};
              queueMicrotask(() => {
                instance.slideTo = originalSlideTo;
              });
            },
            true
          );
        });
      }

      /**
       * Lazy brand panels — placeholders carry data-fg-hmm-src pointing at
       * sections/fg-hmm-panel.liquid (Section Rendering API). Injection
       * always targets the given panel element, never "the active one",
       * so rapid A→B hovers can't cross-populate panels. Response text is
       * cached per src (one vendor can serve panels in several menus; each
       * injection freshly upgrades its menu-product-list elements).
       */
      hydratePanel(panel) {
        if (!panel || !panel.hasAttribute('data-fg-hmm-lazy')) return Promise.resolve();
        const src = panel.getAttribute('data-fg-hmm-src');
        if (!src) return Promise.resolve();

        const inject = (html) => {
          if (!panel.hasAttribute('data-fg-hmm-lazy')) return;
          const template = document.createElement('template');
          template.innerHTML = html;
          const sectionEl = template.content.querySelector('.shopify-section') || template.content.firstElementChild;
          if (!sectionEl) return;
          // Element upgrades inside injected content (Swiper init, lazy
          // images) can scroll the menu's columns back to the top when the
          // panel is visible at injection time. Snapshot and restore.
          const scrollers = [];
          this.querySelectorAll('.fg-hmm__middle-panel, .fg-hmm__right').forEach((s) => {
            if (s.scrollTop > 0) scrollers.push([s, s.scrollTop]);
          });
          const restoreScroll = () => {
            scrollers.forEach(([s, top]) => {
              if (s.scrollTop !== top) s.scrollTop = top;
            });
          };
          // sections/fg-hmm-panel derives its heading from the collection
          // title, but a tab's default row is headed by its configured
          // products_heading ("Best Sellers"). The stub carries it over.
          const headingOverride = panel.getAttribute('data-fg-hmm-heading');
          panel.innerHTML = sectionEl.innerHTML;
          if (headingOverride) {
            const headingEl = panel.querySelector('.fg-hmm__products-heading');
            if (headingEl) headingEl.textContent = headingOverride;
          }
          panel.removeAttribute('data-fg-hmm-lazy');
          panel.removeAttribute('data-fg-hmm-src');
          panel.removeAttribute('data-fg-hmm-heading');
          restoreScroll();
          window.requestAnimationFrame(() => {
            this.hardenCarousels(panel);
            panel.querySelectorAll('.swiper').forEach((swiperEl) => {
              if (swiperEl.swiper) swiperEl.swiper.update();
            });
            restoreScroll();
          });
        };

        // data-fg-hmm-error flips the panel from skeleton to the view-all
        // fallback (CSS). Clear it up front so a retry hover shows the
        // skeleton again while the new fetch runs.
        panel.removeAttribute('data-fg-hmm-error');

        if (this._panelHtml.has(src)) {
          inject(this._panelHtml.get(src));
          return Promise.resolve();
        }
        if (this._panelInflight.has(src)) {
          // Another panel started this fetch (same vendor in two tabs, or
          // hover racing prefetch) — chain THIS panel's injection onto it,
          // otherwise the second panel stays a skeleton forever.
          return this._panelInflight.get(src).then(() => {
            if (this._panelHtml.has(src)) inject(this._panelHtml.get(src));
            else panel.setAttribute('data-fg-hmm-error', '');
          });
        }

        const promise = fetch(src)
          .then((response) => {
            if (!response.ok) throw new Error(`fg-hmm-panel ${response.status}`);
            return response.text();
          })
          .then((html) => {
            this._panelHtml.set(src, html);
            this._panelInflight.delete(src);
            inject(html);
          })
          .catch(() => {
            // Swap skeleton for the view-all fallback; next hover retries.
            this._panelInflight.delete(src);
            panel.setAttribute('data-fg-hmm-error', '');
          });
        this._panelInflight.set(src, promise);
        return promise;
      }

      prefetchTabPanels(tabIndex) {
        const productsPanel = this.getProductsPanel(tabIndex);
        if (!productsPanel) return;
        // Only hydrate what's visible — brand panels fetch on hover.
        const active = Array.from(
          productsPanel.querySelectorAll('[data-fg-hmm-brand-products][data-fg-hmm-lazy]')
        ).find((el) => el.classList.contains('is-active'));
        if (active) {
          this.hydratePanel(active);
          return;
        }
        const fallback = this.findBrandPanel(productsPanel, -1);
        if (fallback?.hasAttribute('data-fg-hmm-lazy')) this.hydratePanel(fallback);
      }

      activateTab(index, { force = false, resetBrand = true } = {}) {
        if (Number.isNaN(index)) return;
        if (!force && index === this.activeIndex) return;

        // A pending brand hover from the previous tab must not fire late.
        this.cancelHoverActivation();
        this.activeIndex = index;

        this.tabs.forEach((tab) => {
          const tabIndex = Number(tab.getAttribute('data-fg-hmm-tab'));
          const isActive = tabIndex === index;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        this.middlePanels.forEach((panel) => {
          const tabIndex = Number(panel.getAttribute('data-fg-hmm-panel'));
          const isActive = tabIndex === index;
          panel.classList.toggle('is-active', isActive);
          panel.toggleAttribute('hidden', !isActive);
        });

        this.productPanels.forEach((panel) => {
          const tabIndex = Number(panel.getAttribute('data-fg-hmm-products'));
          const isActive = tabIndex === index;
          panel.classList.toggle('is-active', isActive);
          panel.toggleAttribute('hidden', !isActive);
        });

        // Default products must not wait on the brands SRA fetch.
        if (resetBrand) {
          this.showDefaultProducts(index);
        } else {
          this.prefetchTabPanels(index);
        }

        this.hydrateBrands(index).then(() => {
          if (this.activeIndex !== index) return;

          const panel = this.getProductsPanel(index);
          if (!panel?.hasAttribute('data-fg-hmm-has-brands')) {
            this.updateActiveCarousel(index);
            return;
          }

          // Only honor an intentional middle-link hover (indices ≥ 1000).
          const hoveredTrigger = this.querySelector('[data-fg-hmm-brand-trigger]:hover');
          if (hoveredTrigger && this.contains(hoveredTrigger)) {
            const brandIndex = Number(hoveredTrigger.getAttribute('data-fg-hmm-brand'));
            if (!Number.isNaN(brandIndex) && brandIndex >= 1000) {
              this.activateBrand(index, brandIndex, true);
              return;
            }
          }

          // Always restore the default row after brand logos arrive.
          this.showDefaultProducts(index);
        });
      }

      /**
       * Image tabs default to their first rendered brand (context filtering
       * may skip slots, so indices can have gaps — never assume 0 exists).
       * Link tabs with attached brand sets default to the tab's own product
       * list, rendered as panel -1.
       */
      getDefaultBrandIndex(tabIndex) {
        const productsPanel = this.getProductsPanel(tabIndex);
        if (this.findBrandPanel(productsPanel, -1)) return -1;
        const firstTrigger = this.getMiddlePanel(tabIndex)?.querySelector('[data-fg-hmm-brand-trigger]');
        const index = firstTrigger ? Number(firstTrigger.getAttribute('data-fg-hmm-brand')) : 0;
        return Number.isNaN(index) ? 0 : index;
      }

      activateBrand(tabIndex, brandIndex, force = false) {
        const middlePanel = this.getMiddlePanel(tabIndex);
        const productsPanel = this.getProductsPanel(tabIndex);
        if (!middlePanel || !productsPanel) return;

        if (!force && this.activeIndex !== tabIndex && !middlePanel.classList.contains('is-active')) return;
        this.activeIndex = tabIndex;

        let targetPanel = this.findBrandPanel(productsPanel, brandIndex);
        // Never hide the default row for a brand index that has no panel stub.
        if (!targetPanel && brandIndex !== -1) {
          brandIndex = this.findBrandPanel(productsPanel, -1) ? -1 : brandIndex;
          targetPanel = this.findBrandPanel(productsPanel, brandIndex);
        }
        if (targetPanel) this.hydratePanel(targetPanel);

        middlePanel.querySelectorAll('[data-fg-hmm-brand-trigger]').forEach((trigger) => {
          const index = Number(trigger.getAttribute('data-fg-hmm-brand'));
          trigger.classList.toggle('is-active', index === brandIndex);
        });

        productsPanel.querySelectorAll('[data-fg-hmm-brand-products]').forEach((brandPanel) => {
          const index = Number(brandPanel.getAttribute('data-fg-hmm-brand-products'));
          const isActive = index === brandIndex;
          brandPanel.classList.toggle('is-active', isActive);
          brandPanel.toggleAttribute('hidden', !isActive);
        });

        const activeBrandPanel = this.findBrandPanel(productsPanel, brandIndex);
        activeBrandPanel?.querySelectorAll('.swiper').forEach((swiperEl) => {
          if (swiperEl.swiper) swiperEl.swiper.update();
        });
      }

      updateActiveCarousel(index) {
        const panel = this.getProductsPanel(index);
        if (!panel) return;

        panel.querySelectorAll('.swiper').forEach((swiperEl) => {
          if (swiperEl.swiper) {
            swiperEl.swiper.update();
          }
        });
      }
    }

    FgHoverMegaMenu._panelHtml = new Map();
    FgHoverMegaMenu._panelInflight = new Map();
    FgHoverMegaMenu._brandsHtml = new Map();
    FgHoverMegaMenu._brandsInflight = new Map();

    customElements.define('fg-hover-mega-menu', FgHoverMegaMenu);
  }

/**
 * Mobile drawer product rows — hydrates the stubs rendered by
 * snippets/fg-hover-mega-menu-drawer.liquid from the
 * sections/fg-hmm-drawer-products SRA endpoint. One capture-phase document
 * listener covers every menu (`toggle` doesn't bubble, but capture still
 * sees it); the first open of a menu's <details> is the trigger — nothing
 * is fetched on page load, and desktop visitors never fetch at all.
 */
(function () {
  if (window.fgHmmDrawerProducts) return;
  window.fgHmmDrawerProducts = true;

  // Keyed by src so two menus pointing at the same collection share one
  // request, whether it has finished or is still in flight.
  var responses = new Map();

  function inject(stub, html) {
    if (!stub.hasAttribute('data-fg-hmm-src')) return;
    var template = document.createElement('template');
    template.innerHTML = html;
    var section = template.content.querySelector('.shopify-section') || template.content.firstElementChild;
    if (!section) return;
    stub.innerHTML = section.innerHTML;
    stub.removeAttribute('data-fg-hmm-src');
  }

  function hydrate(stub) {
    var src = stub.getAttribute('data-fg-hmm-src');
    if (!src) return;
    stub.removeAttribute('data-fg-hmm-error');

    var request = responses.get(src);
    if (!request) {
      request = fetch(src).then(function (response) {
        if (!response.ok) throw new Error('fg-hmm-drawer-products ' + response.status);
        return response.text();
      });
      responses.set(src, request);
    }

    request
      .then(function (html) {
        inject(stub, html);
      })
      .catch(function () {
        // Skeleton out, "View all" in; a later open retries the fetch.
        responses.delete(src);
        stub.setAttribute('data-fg-hmm-error', '');
      });
  }

  document.addEventListener(
    'toggle',
    function (event) {
      var details = event.target;
      if (!details.open || !details.matches || !details.matches('details[is="menu-drawer-details"]')) return;
      details.querySelectorAll('[data-fg-hmm-drawer-products][data-fg-hmm-src]').forEach(hydrate);
    },
    true
  );
})();
